import { Activity, Loader2 } from 'lucide-react';
import { useCallback, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import {
  AdminSectionCard,
  AdminStatusPill,
  ConfirmDialog,
  EmptyState,
  HelpAnchor,
  InlineError,
  Pill,
  SkeletonCardGrid,
  SkeletonRows,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { ADMIN_ACTION_BUTTON } from '@/shared/ui-class';
import {
  SEED_SYSTEM_STATUS,
  type SandboxGcSweepCounters,
  type VecGcSweepCounters,
} from '@/versions/v1/shared/admin-seed';
import { AdminSceneShell } from './admin-shared';
import {
  allowlistLabel,
  formatRelativeMs,
  formatTimestamp,
  formatUptime,
  mockSandboxGcSweep,
  mockVecGcSweep,
  touchGcSnapshot,
  touchSandboxSnapshot,
} from './SystemStatusScene.transform';

function MetricRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-divider py-1.5 text-sm last:border-0">
      <span className="text-muted">{label}</span>
      <span className="text-right text-zinc-900 dark:text-zinc-100">{value}</span>
    </div>
  );
}

export function SystemStatusScene() {
  const [snapshot, setSnapshot] = useState(SEED_SYSTEM_STATUS);
  const [loading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vecResult, setVecResult] = useState<VecGcSweepCounters | null>(null);
  const [sandboxResult, setSandboxResult] = useState<SandboxGcSweepCounters | null>(
    null,
  );
  const [sweepError, setSweepError] = useState<string | null>(null);
  const [vecBusy, setVecBusy] = useState(false);
  const [sandboxBusy, setSandboxBusy] = useState(false);

  const loadStatus = useCallback(() => {
    setError(null);
    setSnapshot(SEED_SYSTEM_STATUS);
  }, []);

  const runVecGc = useCallback(async () => {
    setSweepError(null);
    setVecBusy(true);
    setVecResult(mockVecGcSweep());
    setSnapshot((prev) => touchGcSnapshot(prev));
    setVecBusy(false);
  }, []);

  const runSandboxGc = useCallback(async () => {
    setSweepError(null);
    setSandboxBusy(true);
    setSandboxResult(mockSandboxGcSweep());
    setSnapshot((prev) => touchSandboxSnapshot(prev));
    setSandboxBusy(false);
  }, []);

  const panel = (() => {
    if (loading && !snapshot) {
      return (
        <div className="space-y-6">
          <SkeletonCardGrid count={3} cols={3} />
          <SkeletonRows count={2} cols={5} />
        </div>
      );
    }

    if (error && !snapshot) {
      return (
        <EmptyState
          icon={<Activity className="h-8 w-8 text-zinc-400" />}
          title="Couldn't load system status"
          description={error ?? 'No status data came back.'}
          action={
            <button
              className={ADMIN_ACTION_BUTTON}
              onClick={() => loadStatus()}
              type="button"
            >
              Try again
            </button>
          }
        />
      );
    }

    if (!snapshot) {
      return (
        <EmptyState
          icon={<Activity className="h-8 w-8 text-zinc-400" />}
          title="Couldn't load system status"
          description="No status data came back."
          action={
            <button
              className={ADMIN_ACTION_BUTTON}
              onClick={() => loadStatus()}
              type="button"
            >
              Try again
            </button>
          }
        />
      );
    }

    const { readiness, db, telemetry, gc, deploy, registrySwitches } = snapshot;

    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <AdminStatusPill status={readiness.status} dot />
          <span className="text-sm text-zinc-600 dark:text-zinc-300">
            {readiness.readyForTraffic
              ? 'Ready for traffic'
              : 'Not ready for traffic'}
          </span>
        </div>
        <p className="mt-1 text-xs text-muted">
          Auto-refreshes every 30s while this tab is focused. Telemetry counters
          are process-local on multi-worker deploys.
        </p>

        {error ? <InlineError>{error}</InlineError> : null}

        <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
          <AdminSectionCard title="Database">
            <MetricRow label="Dialect" value={db.dialect} />
            <MetricRow
              label="Pool"
              value={
                db.pool === 'ready' ? (
                  <Pill tone="success">Ready</Pill>
                ) : (
                  <Pill tone="warning">Pending</Pill>
                )
              }
            />
            <MetricRow
              label="Readiness db"
              value={
                readiness.db === 'ready' ? (
                  <Pill tone="success">Ready</Pill>
                ) : (
                  <Pill tone="warning">Pending</Pill>
                )
              }
            />
          </AdminSectionCard>

          <AdminSectionCard title="Process">
            <MetricRow label="Uptime" value={formatUptime(deploy.uptime_ms)} />
            <MetricRow label="Node" value={deploy.nodeVersion} />
            <MetricRow label="Version" value={deploy.version} />
            <MetricRow
              label="Build"
              value={
                deploy.buildId ? deploy.buildId.slice(0, 7) : 'Not set'
              }
            />
            <MetricRow
              label="Listen"
              value={
                readiness.listen === 'ready' ? (
                  <Pill tone="success">Ready</Pill>
                ) : readiness.listen === 'pending' ? (
                  <Pill tone="warning">Pending</Pill>
                ) : (
                  <Pill tone="default">n/a</Pill>
                )
              }
            />
          </AdminSectionCard>

          <AdminSectionCard title="Telemetry">
            <MetricRow
              label="Last Axiom error"
              value={formatRelativeMs(telemetry.lastAxiomErrorAt)}
            />
            <MetricRow
              label="Last PostHog error"
              value={formatRelativeMs(telemetry.lastPostHogErrorAt)}
            />
            <MetricRow
              label="Dropped fields"
              value={telemetry.axiomDroppedFieldCount}
            />
            <MetricRow
              label="Orphan expired"
              value={telemetry.orphanExpiredCount}
            />
            <MetricRow
              label="Skills scratch reclaimed"
              value={telemetry.skillsScratchReclaimedCount}
            />
            <p className="mt-2 text-xs text-muted">
              Dropped-field count bumps when a log key is outside{' '}
              <code className="text-zinc-600 dark:text-zinc-300">
                TELEMETRY_ALLOWLIST_FIELDS
              </code>{' '}
              in{' '}
              <code className="text-zinc-600 dark:text-zinc-300">
                src/lib/telemetry.ts
              </code>
              .
            </p>
          </AdminSectionCard>
        </div>

        <AdminSectionCard
          title="GC sweeps (7-day)"
          description="On-demand sweeps proxy the existing internal GC routes. Results are audited with numeric counters only."
        >
          {sweepError ? <InlineError className="mb-3">{sweepError}</InlineError> : null}
          <Table wrap={false}>
            <THead>
              <TR>
                <TH>Sweep</TH>
                <TH>Last run</TH>
                <TH>Runs</TH>
                <TH>Reclaimed</TH>
                <TH aria-hidden />
              </TR>
            </THead>
            <TBody>
              <TR>
                <TD className="font-medium text-zinc-900 dark:text-zinc-100">
                  Vector GC
                </TD>
                <TD>{formatTimestamp(gc.vecGc.lastRunAt)}</TD>
                <TD>{gc.vecGc.runsLastNDays}</TD>
                <TD>{gc.vecGc.orphansLastNDays} orphans</TD>
                <TD className="text-right">
                  <ConfirmDialog
                    title="Run vector GC"
                    description="Scan the vec table and purge orphan rowids. Safe to re-run; may load the database during peak traffic."
                    confirmLabel="Run sweep"
                    onConfirm={runVecGc}
                  >
                    <button
                      type="button"
                      disabled={vecBusy}
                      className={ADMIN_ACTION_BUTTON}
                    >
                      {vecBusy ? (
                        <span className="inline-flex items-center gap-1">
                          <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                          Running…
                        </span>
                      ) : (
                        'Run now'
                      )}
                    </button>
                  </ConfirmDialog>
                </TD>
              </TR>
              <TR>
                <TD className="font-medium text-zinc-900 dark:text-zinc-100">
                  Sandbox sweeper
                </TD>
                <TD>{formatTimestamp(gc.sandboxSweeper.lastRunAt)}</TD>
                <TD>{gc.sandboxSweeper.runsLastNDays}</TD>
                <TD>
                  {gc.sandboxSweeper.hibernatedLastNDays} hibernated ·{' '}
                  {gc.sandboxSweeper.destroyedLastNDays} destroyed
                </TD>
                <TD className="text-right">
                  <ConfirmDialog
                    title="Run sandbox sweeper"
                    description="Hibernate idle sandboxes and destroy stale ones. Safe to re-run; may call the sandbox provider."
                    confirmLabel="Run sweep"
                    onConfirm={runSandboxGc}
                  >
                    <button
                      type="button"
                      disabled={sandboxBusy}
                      className={ADMIN_ACTION_BUTTON}
                    >
                      {sandboxBusy ? (
                        <span className="inline-flex items-center gap-1">
                          <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                          Running…
                        </span>
                      ) : (
                        'Run now'
                      )}
                    </button>
                  </ConfirmDialog>
                </TD>
              </TR>
            </TBody>
          </Table>
          {vecResult ? (
            <p className="mt-3 text-xs text-zinc-600 dark:text-zinc-300">
              Vector GC: scanned {vecResult.scanned}, orphans {vecResult.orphans},
              purged {vecResult.purged} ({vecResult.durationMs}ms)
            </p>
          ) : null}
          {sandboxResult ? (
            <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-300">
              Sandbox: hibernated {sandboxResult.hibernated}, destroyed{' '}
              {sandboxResult.destroyed}, errors {sandboxResult.errors} (
              {sandboxResult.durationMs}ms)
            </p>
          ) : null}
        </AdminSectionCard>

        <AdminSectionCard title="Registry policy (read-only)">
          <div className="flex flex-wrap gap-2">
            <Link to="/admin/signups" className="group rounded-full">
              <Pill className="transition-colors group-hover:bg-zinc-200 dark:group-hover:bg-zinc-700">
                Lobby:{' '}
                {registrySwitches.signupLobby === 'default'
                  ? 'Default'
                  : registrySwitches.signupLobby.enabled
                    ? 'On'
                    : 'Off'}
              </Pill>
            </Link>
            <Link to="/admin/connectors" className="group rounded-full">
              <Pill className="transition-colors group-hover:bg-zinc-200 dark:group-hover:bg-zinc-700">
                Connectors: {allowlistLabel(registrySwitches.allowedConnectors)}
              </Pill>
            </Link>
            <Link to="/admin/ai-models" className="group rounded-full">
              <Pill className="transition-colors group-hover:bg-zinc-200 dark:group-hover:bg-zinc-700">
                Claude models:{' '}
                {allowlistLabel(registrySwitches.allowedClaudeModels)}
              </Pill>
            </Link>
            <Link to="/admin/ai-models" className="group rounded-full">
              <Pill className="transition-colors group-hover:bg-zinc-200 dark:group-hover:bg-zinc-700">
                Google models:{' '}
                {allowlistLabel(registrySwitches.allowedGoogleModels)}
              </Pill>
            </Link>
            <Link to="/admin/builtin-tools" className="group rounded-full">
              <Pill className="transition-colors group-hover:bg-zinc-200 dark:group-hover:bg-zinc-700">
                Tool baseline:{' '}
                {registrySwitches.builtinToolsBaseline === 'default'
                  ? 'Default'
                  : `${registrySwitches.builtinToolsBaseline.alwaysOnCount} on / ${registrySwitches.builtinToolsBaseline.alwaysOffCount} off`}
              </Pill>
            </Link>
          </div>
        </AdminSectionCard>
      </div>
    );
  })();

  return (
    <AdminSceneShell
      sceneName="SystemStatusScene"
      title="System status"
      description="Deployment readiness, database, telemetry, and on-demand GC sweeps. Auto-refreshes every 30s."
      headerActions={<HelpAnchor doc="workspace/admin" />}
    >
      <Commentable name="Panel">{panel}</Commentable>
    </AdminSceneShell>
  );
}
